import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { get as _get, has as _has } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { Prediction } from '../../+state/geo-coder/geo-coder.models';
import { SearchedValue } from '../../+state/service-locator/service-locator.models';
import { AutofillDropdownService } from '../../services/autofill-dropdown.service';

@Component({
  selector: 'drp-search-by-autocomplete-form',
  templateUrl: './search-by-autocomplete-form.component.html',
  styleUrls: ['./search-by-autocomplete-form.component.scss'],
  standalone: false
})
export class SearchByAutocompleteFormComponent implements OnInit, OnChanges {
  @Input() selectedPredictionAddress;

  // Flag
  @Input() isPredicting: boolean;

  /** List of predictions from Geo API based on what user has typed */
  @Input() predictions: Array<Prediction>;

  /** The current value of the search by name form to emit on submit */
  @Output() searchedValueChange = new EventEmitter<SearchedValue>();

  /** The current value of the form as the user is typing to emit*/
  @Output() requestAutocompleteEvent = new EventEmitter<string>();

  @Output() predictionSelected = new EventEmitter<Prediction>();

  formGroup: UntypedFormGroup;

  private stopValueListener$;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dropdown: AutofillDropdownService
  ) {}

  ngOnInit() {
    this.setupForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      _get(changes, 'selectedPredictionAddress.currentValue') &&
      !changes.selectedPredictionAddress.isFirstChange()
    ) {
      const line1 = _get(
        this.selectedPredictionAddress,
        'address_line1',
        this.selectedPredictionAddress.formatted_address.split(',')[0]
      );
      const city = _get(this.selectedPredictionAddress, 'city', '');
      const state = _get(this.selectedPredictionAddress, 'stateCode', '');
      const zip = _get(this.selectedPredictionAddress, 'zip', '');
      const distance = '25';
      this.searchedValueChange.emit({
        line1: line1,
        city: city,
        state: state,
        zip: zip,
        distance: distance
      });
    }
  }

  searchSubmit(searchValue: string) {
    if (searchValue && searchValue !== '') {
      this.requestAutocompleteEvent.emit(searchValue);
    } else {
      this.dropdown.hide();
    }
  }

  onPredictionSelect(prediction: Prediction) {
    // hide the dropdown values
    this.dropdown.hide();

    // terminate the value listener
    this.detachChangeListener();

    // Update the form value
    this.formGroup.get('searchValue').setValue(prediction.description);

    // TODO: Emit the selection to the parent component to get the lat / long using geoCoderSvc
    this.predictionSelected.emit(prediction);

    // Re-enablet the form listener
    this.attachChangeListener();
  }

  private setupForm() {
    this.formGroup = this.formBuilder.group({
      searchValue: ['', []]
    });

    // Listen to value changes to fire the autocomplete action
    this.attachChangeListener();
  }

  private attachChangeListener() {
    this.stopValueListener$ = new Subject<void>();
    this.formGroup.controls.searchValue.valueChanges
      .pipe(
        // Stop subject will terminate the subscription
        takeUntil(this.stopValueListener$),
        // debounce input for 300 milliseconds
        debounceTime(300),
        // only emit if emission is different from previous emission
        distinctUntilChanged()
      )
      .subscribe(searchValue => {
        this.searchSubmit(searchValue);
      });
  }

  private detachChangeListener() {
    this.stopValueListener$.next();
    this.stopValueListener$.complete();
  }
}
